/**
 * Social feed mobile autoplay — only the currently visible card plays.
 * Listens to slider:change (horizontal swipe) and IntersectionObserver (enter/exit viewport).
 * Only active on mobile (< 768px). Does not touch desktop behaviour.
 */
(function () {
  'use strict';

  var BREAKPOINT = 768;

  /* ── helpers ─────────────────────────────────────────────────────── */

  function getVideoMedia(card) {
    return card ? card.querySelector('video-media') : null;
  }

  function playVM(vm) {
    if (!vm) return;
    // DeferredMedia.play() loads the <template> synchronously (playerTarget),
    // then queues the HTMLVideoElement.play() as a microtask.
    vm.play();
    // Mute here — runs before the microtask, so browsers allow autoplay.
    var video = vm.querySelector('video');
    if (video) {
      video.muted = true;
      video.setAttribute('playsinline', '');
    }
  }

  function pauseVM(vm) {
    if (!vm) return;
    vm.pause();
  }

  /* ── FeedAutoplay class ───────────────────────────────────────────── */

  function FeedAutoplay(slider) {
    this.slider = slider;
    this.cards = Array.from(slider.querySelectorAll('.card'));
    this._onSliderChange = this._onSliderChange.bind(this);
    this._onViewport = this._onViewport.bind(this);
    this._setup();
  }

  FeedAutoplay.prototype._setup = function () {
    // Primary: react to horizontal swipe / arrow navigation
    this.slider.addEventListener('slider:change', this._onSliderChange);

    // Secondary: play first card when section scrolls into view,
    // pause everything when section scrolls out of view.
    this.viewportObserver = new IntersectionObserver(this._onViewport, {
      threshold: 0.3
    });
    this.viewportObserver.observe(this.slider);
  };

  FeedAutoplay.prototype._onSliderChange = function (e) {
    var activeCard = e.detail && e.detail.currentElement;
    if (!activeCard) return;
    this._updatePlayback(activeCard);
  };

  FeedAutoplay.prototype._onViewport = function (entries) {
    var entry = entries[0];
    if (entry.isIntersecting) {
      // Play whichever card is currently active (page 1 on first enter)
      var page = (this.slider.currentPage || 1) - 1;
      var activeCard = this.cards[page] || this.cards[0];
      this._updatePlayback(activeCard);
    } else {
      this._pauseAll();
    }
  };

  FeedAutoplay.prototype._updatePlayback = function (activeCard) {
    var self = this;
    self.cards.forEach(function (card) {
      var vm = getVideoMedia(card);
      if (!vm) return;
      if (card === activeCard) {
        playVM(vm);
      } else {
        pauseVM(vm);
      }
    });
  };

  FeedAutoplay.prototype._pauseAll = function () {
    this.cards.forEach(function (card) {
      pauseVM(getVideoMedia(card));
    });
  };

  FeedAutoplay.prototype.destroy = function () {
    this.slider.removeEventListener('slider:change', this._onSliderChange);
    if (this.viewportObserver) {
      this.viewportObserver.disconnect();
      this.viewportObserver = null;
    }
    this._pauseAll();
  };

  /* ── lifecycle ────────────────────────────────────────────────────── */

  function isMobile() {
    return window.innerWidth < BREAKPOINT;
  }

  function mount() {
    if (!isMobile()) return;
    document.querySelectorAll('[data-mobile-feed]').forEach(function (slider) {
      if (!slider._feedAutoplay) {
        slider._feedAutoplay = new FeedAutoplay(slider);
      }
    });
  }

  function unmountSlider(slider) {
    if (slider && slider._feedAutoplay) {
      slider._feedAutoplay.destroy();
      delete slider._feedAutoplay;
    }
  }

  function unmountSection(target) {
    var slider = target.matches && target.matches('[data-mobile-feed]')
      ? target
      : target.querySelector && target.querySelector('[data-mobile-feed]');
    unmountSlider(slider);
  }

  function unmountAll() {
    document.querySelectorAll('[data-mobile-feed]').forEach(unmountSlider);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', mount);
  } else {
    mount();
  }

  document.addEventListener('shopify:section:load', function (e) {
    unmountSection(e.target);
    mount();
  });
  document.addEventListener('shopify:section:unload', function (e) {
    unmountSection(e.target);
  });

  var resizeTimer;
  window.addEventListener('resize', function () {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(function () {
      if (isMobile()) {
        mount();
      } else {
        unmountAll();
      }
    }, 250);
  });
})();
